// Blog posts data
export const posts = [
  {
    id: '49ers-analyst',
    hidden: true,
    title: 'A Sports Analyst Agent for the 49ers Playoff Run',
    date: '2026-01-14',
    description: 'I built a multi-LLM AI agent to scout the 49ers playoff opponents. xAI for Twitter, Gemini for film, GPT-5.2 for stats, Opus 4.5 for synthesis. Here\'s what it found for the divisional round vs Seattle.',
    image: 'https://keshavrao.com/og-49ers.png',
    tags: ['49ers', 'NFL', 'AI', 'LLM', 'sports analytics', 'playoffs'],
    content: `The 49ers have the hardest path to the Super Bowl.

6 seed. On the road. Injured. Underdogs every round.

But a cinderella story is brewing. So I built an AI sports analyst to find the edge.

---

## The System

Anyone can ask ChatGPT about the Seahawks. The edge is knowing *which* reporters to trust, *which* film breakdowns matter, *which* stats actually predict outcomes. Curation beats raw data.

So I built a multi-LLM system where each model does what it's best at:

- **xAI API** pulls real-time tweets from Lombardi, Maiocco, Grant Cohn (the beat reporters who actually know the team)
- **Gemini** processes YouTube film breakdowns, natively multimodal and built for video
- **GPT-5.2** powers a browser agent I built to scrape PFF and ESPN for stats, fast and cheap for high-volume tasks
- **Opus 4.5** synthesizes everything into scouting reports, the best reasoning model for connecting dots

5 minutes of research. Full scouting report. I can ask it anything.

Here's what it found for the divisional round vs Seattle.

---

## 1. Tonges Over the Middle

Seattle ranks **28th in tight end coverage**. LB Dodson grades 62.4 in coverage per PFF. Jake Tonges already scored the go-ahead TD against them in Week 1.

When Kittle was out earlier this season (Weeks 2-6), Tonges averaged 4.8 catches and 44.8 yards per game with 3 touchdowns. Now with Kittle out again for the playoffs, Tonges is ready to step up as TE1.

**The play:** Attack the seams at 12-18 yards. Be Purdy's safety valve on 3rd down.

---

## 2. The Defense Will Create Turnovers

From David Lombardi (@LombardiHimself):

*"The SF defense Seattle will face should look significantly different from Week 18: Eric Kendricks solidified at MLB, Garret Wallow at WLB, Marques Sigle back at safety."*

The edge: **KJ Wright**, former Seahawks LB (2011-2020), is now on the 49ers coaching staff. Kendricks has been in Wright's office learning Seattle's playbook. They know what's coming.

Sam Darnold has 15 interceptions and 13 fumbles this season. He struggles under pressure. This 49ers defense now has a guy who ran Seattle's schemes for 10 years coaching against them.

**The play:** Blitz. Disguise. Create chaos. The ball will come.

---

## 3. Go Air Raid

From @GrantCohn's film analysis, the contrarian take: **abandon the run game**. 40+ throws. High-volume passing through Tonges, CMC, and Juszczyk.

Seattle's run defense is legit. Their LB coverage? Bottom of the league. Same unit that ranks 28th in TE coverage can't handle CMC out of the backfield either.

**CMC vs Seattle this season:**
- 15 receptions for 107 yards (7.1 Y/R)
- 199 total yards on 45 touches

Week 18 loss: only 11 carries, 28 yards. The ground game didn't work. But CMC is averaging 7.1 yards per catch against them.

**The play:** Quick screens. Wheel routes. Keep it in the air. Let Purdy cook.

---

## 4. What Seattle Will Try (And Why It Won't Work)

Let's call out our weaknesses before they can exploit them.

**The O-line is banged up.** Trent Williams has been playing hurt all season. Seattle will blitz, try to collapse the pocket, force Purdy into bad decisions. They'll bring Dre'Mont Jones and Leonard Williams up the middle.

**Counter:** Purdy's time-to-throw is 2.41 seconds this season, 5th fastest in the NFL. He won't hold the ball. Quick game. RPOs. Get it out before the pressure arrives.

**No Kittle.** Our best receiver is out. Seattle will load the box, dare us to throw outside.

**Counter:** This is where Tonges and the checkdown game win. Seattle's LBs can't cover. We don't need Kittle to beat zone coverage. We need targets who can sit in the soft spots. Tonges, CMC, Juszczyk.

**Purdy struggles vs pressure.** In Week 18, Seattle blitzed on 41% of dropbacks. Purdy went 6/14 for 57 yards against the blitz.

**Counter:** Film study. The Eagles blitzed 38% against us in the Wild Card and Purdy went 7/11 for 89 yards and a TD. The difference? He knew where the blitz was coming. KJ Wright on the sideline means we've seen Seattle's playbook. We know the looks.

**Kenneth Walker can gash us.** He averaged 5.1 YPC against SF this season.

**Counter:** Rookie DTs C.J. West and Alfred Collins have been dominant. Per @nwagoner: when West and Collins are on the field together, Philly had just 2.9 YPC. Walker got 67 yards on 16 carries (4.2 YPC) vs the Rams in the Wild Card. The interior run defense is clicking now.

---

## Example Query

\`\`\`
> What's Seattle's biggest defensive weakness?

[searching twitter: @LombardiHimself @MaioccoNBCS @GrantCohn...]
[scraping pff.com/nfl/teams/seahawks/defense...]
[analyzing youtube: "Seahawks Film Room 2025"...]

SOURCES FOUND:
- @LombardiHimself tweet (Jan 12): "Seattle's LB coverage is bottom 5..."
- PFF Team Defense Grades: SEA ranks 28th in TE coverage
- Film breakdown: Dodson struggles in zone, grades 62.4

SYNTHESIS:
Seattle ranks 28th in tight end coverage. LB Dodson grades 62.4 in coverage per PFF.
Jake Tonges already scored the go-ahead TD against them in Week 1.

RECOMMENDATION: Attack the seams at 12-18 yards. Make Tonges the focal point.
\`\`\`

---

## What's Next

Running this for a few hours each day leading up to Saturday. Ask me anything in the replies and the agent will answer.

**If you're on the Niners and want access, DM me.**

Sports teams that want AI-powered scouting reports, same thing. Bring it to your org.

Go Niners.`
  },
  {
    id: 'the-agent-engineer',
    title: 'The Agent Engineer',
    date: '2026-01',
    content: `The tweet that changed programming forever.

> "I've never felt this much behind as a programmer. The profession is being dramatically refactored as the bits contributed by the programmer are increasingly sparse and between. I have a sense that I could be 10X more powerful if I just properly string together what has become available over the last ~year and a failure to claim the boost feels decidedly like skill issue. There's a new programmable layer of abstraction to master (in addition to the usual layers below) involving agents, subagents, their prompts, contexts, memory, modes, permissions, tools, plugins, skills, hooks, MCP, LSP, slash commands, workflows, IDE integrations, and a need to build an all-encompassing mental model for strengths and pitfalls of fundamentally stochastic, fallible, unintelligible and changing entities suddenly intermingled with what used to be good old fashioned engineering. Clearly some powerful alien tool was handed around except it comes with no manual and everyone has to figure out how to hold it and operate it, while the resulting magnitude 9 earthquake is rocking the profession. Roll up your sleeves to not fall behind."
> — Andrej Karpathy, Dec 26, 2025

The next day, Boris, creator of Claude Code, replied to a thread with this:

> "Correct. In the last thirty days, 100% of my contributions to Claude Code were written by Claude Code"
> — Boris Cherny, Dec 27, 2025

This felt like the moment. But why? Claude Code has been around for a while. Opus 4.5 launched a month earlier. Some combination of winter break free time, tweet mindshare, and recent Claude Code updates. Either way, you could feel it. Everyone was euphoric.

> "It genuinely feels to me like GPT-5.2 and Opus 4.5 in November represent an inflection point - one of those moments where the models get incrementally better in a way that tips across an invisible capability line where suddenly a whole bunch of much harder coding problems open up"
> — Simon Willison

> "ive done more personal coding projects over christmas break than i have in the last 10 years. its crazy. i can sense the limitations, but i *know* nothing is going to be the same anymore."
> — David Holz, Jan 3

Within days, posts like these flooded X. It was clear: software would never be the same. And a new role was emerging.

---

## The Agent Engineer

Instead of writing lines of code, the agent engineer focuses on managing relevant context. Maintaining progress between chat sessions. Coordinating teams of agents and decisions. Having the best models read the latest research papers and apply insights to their harness framework. Forking open source libraries. Understanding the systems behind everything.

Two typical counterpoints:

The first: "AI isn't good enough yet". Boris shipped 100% of Claude Code with Claude Code. We're past that.

The second: AI will automate everything, even taste. And sure, the agents will get really good. But that will create even more opportunity for strong opinions, direction, irrationalities, randomness, and doing it for the love of the game.

The agent engineer is a role that matters. Companies that hire and train agent engineers will see outsized gains in short periods of time. The sooner they start, the faster the returns. A lot of us took no days off winter break for this reason.

Solo founders will have their moment. Those who get good at agent engineering can take on incumbents 100x their size.

Software will change but the skills that matter stay the same. Adapting to new tools. Thinking about thinking. Low ego. Willingness to unlearn and start fresh. Applying opinions and taste.

As we ascend to the stars, cure diseases, find new energy sources, our tools will only get better and we'll have the ability to solve even harder problems.

2026 is the year of the agent engineer.`
  },
  {
    id: 'everyone-will-love-making-their-own-apps',
    hidden: true,
    title: 'Everyone Will Love Making Their Own Apps',
    date: '2025-12',
    content: `It's 2:46am. Four Claude windows open. I've been chasing this problem for years. Ideas come fast, but friction always kills them.

With AI, writing should be easier. It isn't. Chatbots vomit 1000-word essays on what they think you want. Magic at first. Now it's a slop doomhole you pray to escape. Feels inorganic.

But AI excels at what actually matters: organizing chaos, connecting dots, triggering flow, and giving you confidence. Confidence is the big thing.

---

This is the 2:46am feeling of everyone "vibe coding" lately. The models are good enough. We stopped chasing AGI.

Pair the right model with a framework, and something shifts. It's not magic. It's reps. Better questions. Better prompts. Better results.

People will build their own tools. More than anyone expects.

---

Instead of the 25th SaaS subscription, ask: What if I built this myself? It's harder. But maybe that's not what you want anyway.

Personal software evolves. It fits.

Imagine a cancer researcher building her own knowledge synthesis machine. A musician designing a DAW that mirrors how she thinks. When building costs less than adapting, you just build.

---

I wrote this essay using my own tool. Got lost in it. That's the point.

Getting ideas out is brutal. So I built something for it.

`
  },
  {
    id: 'craft-work-invisible-work',
    title: 'Craft & The Invisible Work',
    date: '2025-08',
    content: `There's craft work. There's invisible work.

Craft work takes your full attention. You make opinionated decisions. You build feedback loops. You hit flow state.

Invisible work just needs to get done. The steps don't matter.

This distinction matters now. If your work fits the invisible bucket, rethink what you do.

But if you find craft, work that makes you feel human, protect it.`
  }
];

export function getSortedPostsData() {
  return posts
    .filter((post) => !post.hidden)
    .sort((a, b) => {
      if (a.date < b.date) {
        return 1;
      } else {
        return -1;
      }
    });
}
